import React, { useEffect, useMemo, useState } from 'react';
import './DefineFilterModal.css';

const FIELD_CONFIG = [
  { key: 'OEM', label: 'OEM' },
  { key: 'brand', label: 'Brand' },
  { key: 'car_model', label: 'Car Model' },
  { key: 'system', label: 'System' },
  { key: 'subsystem', label: 'Subsystem' },
  { key: 'part', label: 'Part' },
  { key: 'type', label: 'Type' },
  { key: 'material', label: 'Material' },
  { key: 'technology', label: 'Technology' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'unread', label: 'Non lu' },
  { value: 'read', label: 'Lu' },
];

const emptyFilterSet = {
  OEM: [],
  brand: [],
  car_model: [],
  system: [],
  subsystem: [],
  part: [],
  type: [],
  material: [],
  technology: [],
  year_of_publication: ['', ''],
  status: '',
  query: '',
};

const toStringArray = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  return values
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => String(value))
    .filter((value) => {
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
};

const toYearTuple = (values) => {
  if (!Array.isArray(values) || values.length !== 2) {
    return ['', ''];
  }

  return values.map((value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? String(parsed) : '';
  });
};

const sanitizeYearRange = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }
  const parsed = values
    .map((value) => {
      const parsedValue = Number.parseInt(value, 10);
      return Number.isFinite(parsedValue) ? parsedValue : null;
    })
    .filter((value) => value !== null);

  if (parsed.length !== 2) {
    return [];
  }
  const [start, end] = parsed;
  return start <= end ? parsed : [end, start];
};

const buildInitialFilters = (initialFilters) => {
  const source = initialFilters && typeof initialFilters === 'object' ? initialFilters : {};
  return {
    OEM: toStringArray(source.OEM),
    brand: toStringArray(source.brand),
    car_model: toStringArray(source.car_model),
    system: toStringArray(source.system),
    subsystem: toStringArray(source.subsystem || source.Subsystem),
    part: toStringArray(source.part),
    type: toStringArray(source.type),
    material: toStringArray(source.material),
    technology: toStringArray(source.technology),
    year_of_publication: toYearTuple(source.year_of_publication),
    status: typeof source.status === 'string' ? (source.status === 'new' ? 'unread' : source.status) : '',
    query: typeof source.query === 'string' ? source.query : '',
  };
};

const normalizeOptions = (options) => {
  const base = options && typeof options === 'object' ? options : {};
  return {
    OEM: toStringArray(base.OEM),
    brand: toStringArray(base.brand),
    car_model: toStringArray(base.car_model),
    system: toStringArray(base.system),
    subsystem: toStringArray(base.subsystem || base.Subsystem),
    part: toStringArray(base.part),
    type: toStringArray(base.type),
    material: toStringArray(base.material),
    technology: toStringArray(base.technology),
  };
};

const DatabaseFilterModal = ({
  open,
  options = {},
  initialFilters,
  initialName = '',
  onClose,
  onSave,
  saving = false,
  errorMessage = '',
}) => {
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const [selectedFilters, setSelectedFilters] = useState(() => buildInitialFilters(initialFilters));
  const [filterName, setFilterName] = useState(initialName);

  useEffect(() => {
    if (open) {
      setSelectedFilters(buildInitialFilters(initialFilters));
      setFilterName(initialName || '');
    }
  }, [open, initialFilters, initialName]);

  const handleToggle = (field, value) => {
    setSelectedFilters((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const exists = current.includes(value);
      const nextValues = exists ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [field]: nextValues };
    });
  };

  const handleSelectAll = (field) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [field]: normalizedOptions[field] || [],
    }));
  };

  const handleClearField = (field) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [field]: [],
    }));
  };

  const handleYearChange = (index, value) => {
    const safeValue = value.replace(/[^0-9]/g, '');
    setSelectedFilters((prev) => {
      const nextRange = Array.isArray(prev.year_of_publication) && prev.year_of_publication.length === 2
        ? [...prev.year_of_publication]
        : ['', ''];
      nextRange[index] = safeValue;
      return { ...prev, year_of_publication: nextRange };
    });
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (typeof onSave !== 'function') {
      return;
    }

    onSave({
      name: filterName.trim(),
      filters: {
        OEM: selectedFilters.OEM,
        brand: selectedFilters.brand,
        system: selectedFilters.system,
        subsystem: selectedFilters.subsystem,
        type: selectedFilters.type,
        material: selectedFilters.material,
        year_of_publication: sanitizeYearRange(selectedFilters.year_of_publication),
        status: selectedFilters.status || '',
        query: selectedFilters.query || '',
      },
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div className="define-filter-modal__overlay" role="dialog" aria-modal="true">
      <div className="define-filter-modal__container">
        <div className="define-filter-modal__header">
          <h2>Set a filter</h2>
          <button
            type="button"
            className="define-filter-modal__close"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            X
          </button>
        </div>

        <form className="define-filter-modal__content" onSubmit={handleSave}>
          <label className="define-filter-modal__field">
            <span>Filter name</span>
            <input
              type="text"
              value={filterName}
              onChange={(event) => setFilterName(event.target.value)}
              placeholder="Example: OEM + Battery"
              required
              disabled={saving}
            />
          </label>

          <div className="define-filter-modal__filters">
            {FIELD_CONFIG.map(({ key, label }) => {
              const values = normalizedOptions[key] || [];
              return (
                <div key={key} className="define-filter-modal__group">
                  <div className="define-filter-modal__group-header">
                    <h3>{label}</h3>
                    <div className="define-filter-modal__group-actions">
                      <button
                        type="button"
                        onClick={() => handleSelectAll(key)}
                        disabled={saving || values.length === 0}
                      >
                        Select all
                      </button>
                      <button type="button" onClick={() => handleClearField(key)} disabled={saving}>
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="define-filter-modal__checkbox-list">
                    {values.length === 0 ? (
                      <p className="define-filter-modal__empty">No options available</p>
                    ) : (
                      values.map((value) => {
                        const isChecked = selectedFilters[key]?.includes(value);
                        return (
                          <label key={value} className="define-filter-modal__checkbox">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggle(key, value)}
                              disabled={saving}
                            />
                            <span>{value}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="define-filter-modal__extra">
            <div className="define-filter-modal__group">
              <div className="define-filter-modal__group-header">
                <h3>Year of publication</h3>
                <div className="define-filter-modal__group-actions">
                  <button
                    type="button"
                    onClick={() => setSelectedFilters((prev) => ({ ...prev, year_of_publication: ['', ''] }))}
                    disabled={saving}
                  >
                    Effacer
                  </button>
                </div>
              </div>
              <div className="define-filter-modal__year-fields">
                <label>
                  <span>Min</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={selectedFilters.year_of_publication?.[0] ?? ''}
                    onChange={(event) => handleYearChange(0, event.target.value)}
                    disabled={saving}
                  />
                </label>
                <label>
                  <span>Max</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={selectedFilters.year_of_publication?.[1] ?? ''}
                    onChange={(event) => handleYearChange(1, event.target.value)}
                    disabled={saving}
                  />
                </label>
              </div>
            </div>

            <label className="define-filter-modal__field">
              <span>Status</span>
              <select
                value={selectedFilters.status}
                onChange={(event) => setSelectedFilters((prev) => ({ ...prev, status: event.target.value }))}
                disabled={saving}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="define-filter-modal__field">
              <span>Search text</span>
              <input
                type="text"
                value={selectedFilters.query}
                onChange={(event) => setSelectedFilters((prev) => ({ ...prev, query: event.target.value }))}
                placeholder="Example: battery, aluminum"
                disabled={saving}
              />
            </label>
          </div>

          {errorMessage && <div className="define-filter-modal__error">{errorMessage}</div>}

          <div className="define-filter-modal__footer">
            <button type="button" className="define-filter-modal__secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="define-filter-modal__primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DatabaseFilterModal;
